import React, { useEffect, useRef, useState } from "react";

// ─── Properly size the canvas for the full viewport ─────────────────────────
function sizeCanvas(canvas) {
  const dpr = window.devicePixelRatio || 1;
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = w + "px";
  canvas.style.height = h + "px";
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { w, h, ctx };
}

// ─── Canvas Confetti ────────────────────────────────────────────────────────
function launchConfetti(canvas) {
  const { w, h, ctx } = sizeCanvas(canvas);

  const colors = [
    "#22c55e", "#16a34a", "#4ade80",
    "#facc15", "#fbbf24", "#f59e0b",
    "#3b82f6", "#60a5fa",
    "#ec4899", "#f472b6",
    "#a855f7", "#c084fc",
    "#f97316",
  ];

  const particles = [];
  const PARTICLE_COUNT = 80;

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.6;
    const velocity = 12 + Math.random() * 20;
    particles.push({
      x: w / 2 + (Math.random() - 0.5) * 120,
      y: h * 0.12,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity,
      width: 8 + Math.random() * 8,
      height: 5 + Math.random() * 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 20,
      gravity: 0.25 + Math.random() * 0.15,
      drag: 0.97 + Math.random() * 0.015,
      opacity: 1,
      fadeStart: 40 + Math.random() * 20,
      fadeSpeed: 0.015 + Math.random() * 0.01,
      shape: Math.random() > 0.4 ? "rect" : "circle",
    });
  }

  let animationId;
  let frame = 0;
  let cancelled = false;

  function animate() {
    if (cancelled) return;
    ctx.clearRect(0, 0, w, h);
    let alive = false;

    for (const p of particles) {
      if (p.opacity <= 0) continue;
      alive = true;

      p.vy += p.gravity;
      p.vx *= p.drag;
      p.vy *= p.drag;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.rotationSpeed;

      if (frame > p.fadeStart) {
        p.opacity -= p.fadeSpeed;
      }

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.globalAlpha = Math.max(0, p.opacity);
      ctx.fillStyle = p.color;

      if (p.shape === "rect") {
        ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.width / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    frame++;
    if (alive && frame < 150) {
      animationId = requestAnimationFrame(animate);
    } else {
      ctx.clearRect(0, 0, w, h);
    }
  }

  animate();
  return () => {
    cancelled = true;
    cancelAnimationFrame(animationId);
    try { ctx.clearRect(0, 0, w, h); } catch (e) {}
  };
}

// ─── Bad Deal Flash — red vignette pulse, no particles ──────────────────────
function launchBadDealFlash(canvas) {
  const { w, h, ctx } = sizeCanvas(canvas);

  let animationId;
  let frame = 0;
  let cancelled = false;
  const totalFrames = 90; // ~1.5s

  function animate() {
    if (cancelled) return;
    ctx.clearRect(0, 0, w, h);

    // Two-phase: quick fade in (0-15), hold + fade out (15-90)
    let alpha;
    if (frame < 15) {
      alpha = 0.18 * (frame / 15);
    } else {
      alpha = 0.18 * (1 - (frame - 15) / (totalFrames - 15));
    }

    // Red vignette overlay
    const gradient = ctx.createRadialGradient(w / 2, h / 2, h * 0.15, w / 2, h / 2, h * 0.9);
    gradient.addColorStop(0, `rgba(220, 38, 38, 0)`);
    gradient.addColorStop(0.6, `rgba(220, 38, 38, ${alpha * 0.5})`);
    gradient.addColorStop(1, `rgba(180, 20, 20, ${alpha})`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    frame++;
    if (frame < totalFrames) {
      animationId = requestAnimationFrame(animate);
    } else {
      ctx.clearRect(0, 0, w, h);
    }
  }

  animate();
  return () => {
    cancelled = true;
    cancelAnimationFrame(animationId);
    try { ctx.clearRect(0, 0, w, h); } catch (e) {}
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (value) => {
  if (isNaN(value) || !isFinite(value)) return "$0";
  const absVal = Math.abs(value);
  const formatted = absVal.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
};

// ─── Main Component ─────────────────────────────────────────────────────────

export function DealResultEffect({ totalProfit, isVisible }) {
  const canvasRef = useRef(null);
  const prevVisibleRef = useRef(false);
  const cleanupRef = useRef(null);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerExit, setBannerExit] = useState(false);

  const isGoodDeal = totalProfit >= 0;

  useEffect(() => {
    // Detect transition: isVisible going from false → true
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = isVisible;

    if (!isVisible) {
      // Clean up when leaving
      setShowBanner(false);
      setBannerExit(false);
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      return;
    }

    // Only fire on false→true transition
    if (wasVisible) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Collect all timers/cleanups for this burst
    const timers = [];
    let effectCleanup = null;

    // Wait for the tab content to fully render before launching effects
    // requestIdleCallback lets the browser finish layout/paint first
    const startEffects = () => {
      if (isGoodDeal) {
        effectCleanup = launchConfetti(canvas);
      } else {
        effectCleanup = launchBadDealFlash(canvas);
      }

      // Show banner
      const bannerTimer = setTimeout(() => {
        setShowBanner(true);
        setBannerExit(false);
      }, 150);
      timers.push(bannerTimer);

      // Auto-dismiss banner
      const dismissTimer = setTimeout(() => {
        setBannerExit(true);
        const hideTimer = setTimeout(() => setShowBanner(false), 400);
        timers.push(hideTimer);
      }, 2800);
      timers.push(dismissTimer);
    };

    // Use requestIdleCallback (with setTimeout fallback) so the browser
    // finishes painting the results tab before we start the animation
    let idleId;
    const delayTimer = setTimeout(() => {
      if (window.requestIdleCallback) {
        idleId = window.requestIdleCallback(startEffects, { timeout: 100 });
      } else {
        startEffects();
      }
    }, 200);
    timers.push(delayTimer);

    // Store cleanup
    cleanupRef.current = () => {
      timers.forEach(clearTimeout);
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (effectCleanup) effectCleanup();
    };

    return () => {
      // Component unmount cleanup
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [isVisible, isGoodDeal, totalProfit]);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          pointerEvents: "none",
          zIndex: 9999,
        }}
      />

      {showBanner && (
        <div
          className={`fixed top-0 left-0 right-0 z-[9998] flex justify-center px-4 pt-4 sm:pt-6 ${
            bannerExit ? "deal-banner-exit" : "deal-banner-enter"
          }`}
          style={{ pointerEvents: "none" }}
        >
          <div
            className={`
              px-5 py-3 sm:px-8 sm:py-4 rounded-2xl shadow-2xl backdrop-blur-sm
              flex items-center gap-3 max-w-lg
              ${
                isGoodDeal
                  ? "bg-gradient-to-r from-green-500/95 to-emerald-600/95 text-white"
                  : "bg-gradient-to-r from-red-500/95 to-rose-600/95 text-white"
              }
            `}
          >
            <span className="text-2xl sm:text-3xl flex-shrink-0">
              {isGoodDeal ? "🎉" : "⚠️"}
            </span>
            <div>
              <p className="font-bold text-sm sm:text-base">
                {isGoodDeal ? "Great Deal!" : "Not Profitable"}
              </p>
              <p className="text-xs sm:text-sm opacity-90">
                {isGoodDeal
                  ? `Extra Proceeds: ${formatCurrency(totalProfit)}`
                  : `Loss: ${formatCurrency(totalProfit)}`}
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes deal-slide-in {
          0% { transform: translateY(-100%); opacity: 0; }
          60% { transform: translateY(8px); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes deal-slide-out {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-100%); opacity: 0; }
        }
        .deal-banner-enter {
          animation: deal-slide-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .deal-banner-exit {
          animation: deal-slide-out 0.4s ease-in forwards;
        }
      `}</style>
    </>
  );
}

export default DealResultEffect;
